import { create } from "zustand";
import apiClient from "../services/apiClient";

const useStore = create((set, get) => ({
  records: null,
  profile: null,
  plans: null,
  testComponents: null,
  freeAnalysis: null,
  isLoadingRecords: false,
  isLoadingBiorhythm: false,
  isLoadingProfile: false,
  isLoadingPlans: false,
  isLoadingTestComponents: false,
  isLoadingFreeAnalysis: false,
  errorRecords: null,
  errorProfile: null,
  errorBiorhythm: null,
  errorTestComponents: null,
  errorPlans: null,
  errorFreeAnalysis: null,

  chatHistory: {},

  transactions: null,
  isLoadingTransactions: false,
  errorTransactions: null,

  tempUsers: null,
  isLoadingTempUsers: false,
  errorTempUsers: null,

  statistical: null,
  isLoadingStatistical: false,
  errorStatistical: null,

  birthyearStats: null,
  isLoadingBirthyearStats: false,
  errorBirthyearStats: null,

  profiles: null,
  isLoadingProfiles: false,
  errorProfiles: null,

  tests: null,
  isLoadingTests: false,
  errorTests: null,

  profilesCount: null,
  isLoadingProfilesCount: false,
  errorProfilesCount: null,

  invalidComponents: null,
  isLoadingInvalidComponents: false,
  errorInvalidComponents: null,

  messagesFeedbacks: null,
  messagesTickets: null,
  isLoadingMessages: false,
  errorMessages: null,

  addChatMessage: (testId: string, question: string, answer: string) => {
    set((state: any) => {
      const testHistory = state.chatHistory[testId] || [];
      return {
        chatHistory: {
          ...state.chatHistory,
          [testId]: [
            ...testHistory,
            {
              question,
              answer,
              testId,
              timestamp: Date.now(),
            },
          ],
        },
      };
    });
  },

  getChatHistoryForTest: (testId: string) => {
    const state = get();
    //@ts-ignore
    return state.chatHistory[testId] || [];
  },

  clearChatHistory: (testId?: string) => {
    set((state: any) => {
      if (testId) {
        const newHistory = { ...state.chatHistory };
        delete newHistory[testId];
        return { chatHistory: newHistory };
      } else {
        return { chatHistory: {} };
      }
    });
  },

  // ----------------- App

  fetchRecords: async () => {
    set({ isLoadingRecords: true, errorRecords: null });
    try {
      const response = await apiClient.get("/test-records?page_size=20");
      const data = await response.data;
      set({ records: data });
    } catch (error) {
      set({ errorRecords: "Failed to fetch records." });
    } finally {
      set({ isLoadingRecords: false });
    }
  },

  fetchTestComponents: async () => {
    set({ isLoadingTestComponents: true, errorTestComponents: null });
    try {
      const response = await apiClient.get(
        "/test-records/components/?page_size=400"
      );
      const data = await response.data;
      set({ testComponents: data.results });
    } catch (error) {
      set({ errorTestComponents: "Failed to fetch Test Components." });
    } finally {
      set({ isLoadingTestComponents: false });
    }
  },

  fetchProfile: async () => {
    set({ isLoadingProfile: true, errorProfile: null });
    try {
      const response = await apiClient.get("/profile");
      const data = await response.data;
      set({ profile: data });
    } catch (error) {
      set({ errorProfile: "Failed to fetch profile." });
    } finally {
      set({ isLoadingProfile: false });
    }
  },

  fetchTempUsers: async () => {
    set({ isLoadingTempUsers: true, errorTempUsers: null });
    try {
      const response = await apiClient.get("/profile/temp_user/");
      const data = await response.data;
      set({ tempUsers: data });
    } catch (error) {
      set({ errorTempUsers: "Failed to fetch temp users." });
    } finally {
      set({ isLoadingTempUsers: false });
    }
  },

  fetchBiorhythm: async () => {
    set({ isLoadingBiorhythm: true, errorBiorhythm: null });
    try {
      const response = await apiClient.post(`/profile/biorhythm/`);
      const data = await response.data;
      set({ biorhythm: data });
    } catch (error) {
      set({ errorBiorhythm: "Failed to fetch biorhythm." });
    } finally {
      set({ isLoadingBiorhythm: false });
    }
  },

  fetchPlans: async () => {
    set({ isLoadingPlans: true, errorPlans: null });
    try {
      const response = await apiClient.get(`/financial/plans/`);
      const data = await response.data;
      set({ plans: data });
    } catch (error) {
      set({ errorPlans: "Failed to fetch Plans." });
    } finally {
      set({ isLoadingPlans: false });
    }
  },

  fetchFreeAnalysis: async () => {
    set({ isLoadingFreeAnalysis: true, errorFreeAnalysis: null });
    try {
      const response = await apiClient.get(`/test-records/allow_to_use_free/`);
      const data = await response;
      set({ freeAnalysis: data });
    } catch (error) {
      //@ts-ignore
      set({ errorFreeAnalysis: error?.response?.data });
    } finally {
      set({ isLoadingFreeAnalysis: false });
    }
  },

  fetchTransactions: async () => {
    set({ isLoadingTransactions: true, errorTransactions: null });
    try {
      const response = await apiClient.get(`/transactions/`);
      const data = await response.data;
      set({ transactions: data });
    } catch (error) {
      set({ errorTransactions: "Failed to fetch Transactions." });
    } finally {
      set({ isLoadingTransactions: false });
    }
  },

  //

  // ----------------- Dashboard

  fetchStatistical: async (start_date: string, end_date: string) => {
    set({ isLoadingStatistical: true, errorStatistical: null });
    try {
      const response = await apiClient.get(
        `/admin_api/statistical${
          start_date ? `?start_date=${start_date}` : ""
        }${end_date ? `&end_date=${end_date}` : ""}`
      );
      const data = await response.data;
      set({ statistical: data });
    } catch (error) {
      set({ errorStatistical: "Failed to fetch statistical." });
    } finally {
      set({ isLoadingStatistical: false });
    }
  },

  fetchBirthyearStats: async (start_date: string, end_date: string) => {
    set({ isLoadingBirthyearStats: true, errorBirthyearStats: null });
    try {
      const response = await apiClient.get(
        `/admin_api/profiles/birthyear-stats/${
          start_date ? `?start_date=${start_date}` : ""
        }${end_date ? `&end_date=${end_date}` : ""}`
      );
      const data = await response.data;
      set({ birthyearStats: data });
    } catch (error) {
      set({ errorBirthyearStats: "Failed to fetch BirthyearStats." });
    } finally {
      set({ isLoadingBirthyearStats: false });
    }
  },

  fetchProfiles: async (page: number) => {
    set({ isLoadingProfiles: true, errorProfiles: null });
    try {
      const response = await apiClient.get(`/admin_api/profiles?page=${page}`);
      const data = await response.data;
      set({ profiles: data });
    } catch (error) {
      set({ errorProfiles: "Failed to fetch profiles." });
    } finally {
      set({ isLoadingProfiles: false });
    }
  },

  fetchTests: async (page: number, start_date: string, end_date: string) => {
    set({ isLoadingTests: true, errorTests: null });
    try {
      const response = await apiClient.get(
        `/admin_api/test_records?page=${page}&ordering=-id${
          start_date ? `&start_date=${start_date}` : ""
        }${end_date ? `&end_date=${end_date}` : ""}`
      );
      const data = await response.data;
      set({ tests: data });
    } catch (error) {
      set({ errorTests: "Failed to fetch tests." });
    } finally {
      set({ isLoadingTests: false });
    }
  },

  fetchProfilesCount: async (start_date: string, end_date: string) => {
    set({ isLoadingProfilesCount: true, errorProfilesCount: null });
    try {
      const response = await apiClient.get(
        `/admin_api/profile_count_by_day?start_date=${start_date}&end_date=${end_date}`
      );
      const data = await response.data;
      set({ profilesCount: data });
    } catch (error) {
      set({ errorProfilesCount: "Failed to fetch Profile Count." });
    } finally {
      set({ isLoadingProfilesCount: false });
    }
  },

  fetchInvalidComponents: async () => {
    set({ isLoadingInvalidComponents: true, errorInvalidComponents: null });
    try {
      const response = await apiClient.get(`/admin_api/invalid_components/`);
      const data = await response.data;
      set({ invalidComponents: data });
    } catch (error) {
      set({ errorInvalidComponents: "Failed to fetch Invalid Components." });
    } finally {
      set({ isLoadingInvalidComponents: false });
    }
  },

  fetchMessages: async (model: string) => {
    set({ isLoadingMessages: true, errorMessages: null });
    try {
      const response = await apiClient.get(
        `/admin_api/ticket_or_feedback?page_size=100&model=${model}`
      );
      const data = await response.data;
      if (model === "feedback") {
        set({ messagesFeedbacks: data });
      } else {
        set({ messagesTickets: data });
      }
    } catch (error) {
      set({ errorMessages: "Failed to fetch Messages." });
    } finally {
      set({ isLoadingMessages: false });
    }
  },
}));

export default useStore;
